import { ObterCardapioNovo } from "../../../services/clientefiel"
import {
    cadastrarCategoria,
    cadastrarProduto,
    cadastrarGrupoDeOpcao,
    cadastrarOpcao,
    registrarHistoricoCardapio,
    substituirPorCardapioNovo,
} from "../../../services/cardapio"

// Importa o CSV gerado por exportarCsv.js fazendo UPSERT por id:
// - linha com Id preenchido -> atualiza (mesclando os campos do CSV sobre o item atual,
//   preservando o que não está no CSV);
// - linha com Id vazio -> cria um novo item.
// Hierarquia por linha: C (categoria) > P (produto) > GA/GQ/GD/GO (grupo) > O (opção).

const TOKEN = "a~I?;;+_@!AL^"

// ───────────────────────── Parser CSV (separador ; , aspas RFC-4180) ─────────────────────────

// Detecta o separador olhando a primeira linha: ';' (arquivo aberto no bloco de notas)
// ou TAB (células copiadas do Excel). Usa o que aparecer mais vezes.
function detectarSeparador(texto) {
    const primeiraLinha = texto.split("\n")[0] ?? ""
    const qtdPontoVirgula = (primeiraLinha.match(/;/g) || []).length
    const qtdTab = (primeiraLinha.match(/\t/g) || []).length
    return qtdTab > qtdPontoVirgula ? "\t" : ";"
}

function parseLinhasCsv(texto) {
    // Remove BOM.
    const t = texto.replace(/^﻿/, "")
    const sep = detectarSeparador(t)
    const linhas = []
    let campo = ""
    let linha = []
    let dentroAspas = false
    for (let i = 0; i < t.length; i++) {
        const c = t[i]
        if (dentroAspas) {
            if (c === '"') {
                if (t[i + 1] === '"') {
                    campo += '"'
                    i++
                } else {
                    dentroAspas = false
                }
            } else {
                campo += c
            }
        } else if (c === '"') {
            dentroAspas = true
        } else if (c === sep) {
            linha.push(campo)
            campo = ""
        } else if (c === "\n") {
            linha.push(campo)
            linhas.push(linha)
            linha = []
            campo = ""
        } else if (c === "\r") {
            // ignora; o \n fecha a linha
        } else {
            campo += c
        }
    }
    // Último campo/linha (se o arquivo não terminar com \n).
    if (campo.length > 0 || linha.length > 0) {
        linha.push(campo)
        linhas.push(linha)
    }
    return linhas.filter(l => l.some(v => (v ?? "").trim() !== ""))
}

// Converte as linhas em objetos {coluna: valor} usando o cabeçalho.
function parseCsv(texto) {
    const linhas = parseLinhasCsv(texto)
    if (linhas.length === 0) return []
    const cabecalho = linhas[0].map(h => (h ?? "").trim())
    return linhas.slice(1).map(cols => {
        const obj = {}
        cabecalho.forEach((nome, i) => {
            obj[nome] = cols[i] ?? ""
        })
        return obj
    })
}

// ───────────────────────── Conversões de campo ─────────────────────────

const ehSim = v => /^(sim|s|true|1)/i.test((v ?? "").trim())
const ehNaoExplicito = v => /^(não|nao|n|false|0)/i.test((v ?? "").trim())

const paraBool = (v, padrao) => {
    if (ehSim(v)) return true
    if (ehNaoExplicito(v)) return false
    return padrao
}

const paraNumero = v => {
    const s = (v ?? "").toString().trim().replace(",", ".")
    if (s === "") return null
    const n = parseFloat(s)
    return isNaN(n) ? null : n
}

const paraInt = v => {
    const s = (v ?? "").toString().trim()
    if (s === "") return null
    const n = parseInt(s, 10)
    return isNaN(n) ? null : n
}

const vazio = v => (v ?? "").toString().trim() === ""
const ouNull = v => (vazio(v) ? null : v.trim())

// Letra do grupo -> valor de "adicional" (igual ao backend preencherObjetos).
function adicionalDaLetra(letra) {
    const l = (letra || "").toUpperCase()
    if (l === "GA") return 1
    if (l === "GQ") return 2
    if (l === "GD") return 3
    return 0 // GO
}

// ───────────────────────── Índices do cardápio atual (para merge seguro) ─────────────────────────

function indexarCardapio(categorias) {
    const cat = new Map()
    const prod = new Map()
    const grupo = new Map()
    const opc = new Map()
    ;(categorias ?? []).forEach(c => {
        if (c?.id != null) cat.set(String(c.id), c)
        ;(c.produtos ?? []).forEach(p => {
            if (p?.id != null) prod.set(String(p.id), p)
            ;(p.opcoes ?? []).forEach(g => {
                if (g?.id != null) grupo.set(String(g.id), g)
                ;(g.opcoes ?? []).forEach(o => {
                    if (o?.id != null) opc.set(String(o.id), o)
                })
            })
        })
    })
    return { cat, prod, grupo, opc }
}

// ───────────────────────── Montagem dos payloads (merge sobre o existente) ─────────────────────────

function dataCategoria(linha, existente, ordem, estabelecimento) {
    const base = existente ? { ...existente } : {}
    delete base.produtos
    return {
        ...base,
        token: TOKEN,
        id: existente?.id,
        versao: existente?.versao,
        nome: linha["Nome"],
        ordemExib: existente?.ordemExib ?? ordem,
        ativo: paraBool(linha["Disponível?"], existente?.ativo ?? true),
        quantidadeMinima: paraInt(linha["Qtd. Minima"]) ?? existente?.quantidadeMinima ?? null,
        quantidadeMaxima: paraInt(linha["Qtd. Máxima"]) ?? existente?.quantidadeMaxima ?? null,
        diaSemana: ouNull(linha["Dia da Semana"]) ?? existente?.diaSemana ?? null,
        turnoProduto: ouNull(linha["Turno"]) ?? existente?.turnoProduto ?? null,
        estabelecimento: existente?.estabelecimento ?? estabelecimento,
    }
}

function dataProduto(linha, existente, ordem) {
    const base = existente ? { ...existente } : {}
    delete base.opcoes
    const dispBase = existente?.disponibilidadeProduto ?? {}
    return {
        ...base,
        token: TOKEN,
        id: existente?.id,
        versao: existente?.versao,
        nome: linha["Nome"],
        descricao: linha["Descrição"],
        valor: paraNumero(linha["Preço"]) ?? 0,
        codigo: linha["Codigo"],
        ordemExib: existente?.ordemExib ?? ordem,
        ativo: paraBool(linha["Disponível?"], existente?.ativo ?? true),
        permiteObservacao: paraBool(linha["Permite Observação?"], existente?.permiteObservacao ?? true),
        urlImagem: vazio(linha["Url Foto"]) ? existente?.urlImagem ?? "" : linha["Url Foto"],
        pontos: ouNull(linha["Pontos"]) ?? existente?.pontos ?? null,
        promocional: paraBool(linha["É Promocional?"], existente?.promocional ?? false),
        produtoExibicao: paraBool(linha["Apenas Informativo"], existente?.produtoExibicao ?? false),
        apenasPrimeiraCompra: paraBool(
            linha["Apenas Primeira Compra"],
            existente?.apenasPrimeiraCompra ?? false
        ),
        impressaoAlternada: paraInt(linha["Impressora"]) ?? existente?.impressaoAlternada ?? 0,
        diaSemana: ouNull(linha["Dia da Semana"]) ?? existente?.diaSemana ?? null,
        turnoProduto: ouNull(linha["Turno"]) ?? existente?.turnoProduto ?? null,
        disponibilidadeProduto: {
            ...dispBase,
            valorDomingo: paraNumero(linha["Valor Domingo"]),
            valorSegunda: paraNumero(linha["Valor Segunda"]),
            valorTerca: paraNumero(linha["Valor Terça"]),
            valorQuarta: paraNumero(linha["Valor Quarta"]),
            valorQuinta: paraNumero(linha["Valor Quinta"]),
            valorSexta: paraNumero(linha["Valor Sexta"]),
            valorSabado: paraNumero(linha["Valor Sábado"]),
            disponivelDomingo: paraBool(linha["Disponível Domingo"], true),
            disponivelSegunda: paraBool(linha["Disponível Segunda"], true),
            disponivelTerca: paraBool(linha["Disponível Terça"], true),
            disponivelQuarta: paraBool(linha["Disponível Quarta"], true),
            disponivelQuinta: paraBool(linha["Disponível Quinta"], true),
            disponivelSexta: paraBool(linha["Disponível Sexta"], true),
            disponivelSabado: paraBool(linha["Disponível Sábado"], true),
        },
    }
}

function dataGrupo(linha, existente, ordem) {
    const base = existente ? { ...existente } : {}
    delete base.opcoes
    return {
        ...base,
        id: existente?.id,
        versao: existente?.versao,
        nome: linha["Nome"],
        ordemExib: existente?.ordemExib ?? ordem,
        quantidadeMinima: paraInt(linha["Qtd. Minima"]) ?? existente?.quantidadeMinima ?? null,
        quantidadeMaxima: paraInt(linha["Qtd. Máxima"]) ?? existente?.quantidadeMaxima ?? null,
        considerarMaiorValor: paraBool(
            linha["Considerar Maior Valor?"],
            existente?.considerarMaiorValor ?? false
        ),
        considerarMedia: paraBool(linha["Considerar Média"], existente?.considerarMedia ?? false),
        ativo: paraBool(linha["Disponível?"], existente?.ativo ?? true),
        diaSemana: ouNull(linha["Dia da Semana"]) ?? existente?.diaSemana ?? null,
        adicional: adicionalDaLetra(linha["Tipo"]),
        tipo: ehSim(linha["É Pizza?"]) ? 1 : existente?.tipo ?? 0,
    }
}

function dataOpcao(linha, existente, ordem) {
    const base = existente ? { ...existente } : {}
    return {
        ...base,
        id: existente?.id,
        versao: existente?.versao,
        nome: linha["Nome"],
        descricao: linha["Descrição"],
        valor: paraNumero(linha["Preço"]) ?? 0,
        codigo: linha["Codigo"],
        ordemExib: existente?.ordemExib ?? ordem,
        ativo: paraBool(linha["Disponível?"], existente?.ativo ?? true),
        informativo: paraBool(linha["Apenas Informativo"], existente?.informativo ?? false),
        impressaoAlternada: paraInt(linha["Impressora"]) ?? existente?.impressaoAlternada ?? 0,
        urlImagem: vazio(linha["Url Foto"]) ? existente?.urlImagem ?? "" : linha["Url Foto"],
    }
}

const ehErro = r => !r || r.retornoErro || r.codErro > 0

// Retorna true se o CSV tem a coluna "Id" com ao menos UM valor preenchido.
// Serve para avisar antes de importar sem Ids: sem Id, cada linha vira item NOVO
// (o upsert e por Id), o que duplica o cardapio em vez de atualizar.
export function csvTemIds(texto) {
    const linhas = parseCsv(texto)
    if (linhas.length === 0) return false
    if (!("Id" in linhas[0])) return false
    return linhas.some(l => (l["Id"] ?? "").toString().trim() !== "")
}

// ───────────────────────── Orquestração ─────────────────────────

export async function importarCsvCardapio({ texto, estabelecimento, autorizacao }) {
    const resultado = { criados: 0, atualizados: 0, erros: [] }

    const linhas = parseCsv(texto)
    if (linhas.length === 0) {
        resultado.erros.push("Arquivo vazio ou sem linhas de dados.")
        return resultado
    }
    if (!("Tipo" in linhas[0]) || !("Nome" in linhas[0])) {
        resultado.erros.push(
            "Cabeçalho inválido: use o CSV gerado por 'Exportar CSV (com IDs)'."
        )
        return resultado
    }

    // Carrega o cardápio atual para merge seguro (preserva campos fora do CSV).
    const cardapio = await ObterCardapioNovo(autorizacao, {
        idEstabelecimento: estabelecimento?.id,
        token: TOKEN,
    })
    if (ehErro(cardapio)) {
        resultado.erros.push(cardapio?.mensagem || "Não foi possível carregar o cardápio atual.")
        return resultado
    }
    const idCardapio = cardapio.id
    const idx = indexarCardapio(cardapio.categorias)

    let categoriaIdAtual = null
    let produtoIdAtual = null
    let grupoIdAtual = null
    let ordemC = 0
    let ordemP = 0
    let ordemG = 0
    let ordemO = 0

    const registrar = (resp, existente, contexto) => {
        if (ehErro(resp)) {
            resultado.erros.push(`${contexto}: ${resp?.mensagem || "erro ao salvar"}`)
            return null
        }
        if (existente) resultado.atualizados++
        else resultado.criados++
        return resp
    }

    for (let i = 0; i < linhas.length; i++) {
        const linha = linhas[i]
        const numero = i + 2 // +1 cabeçalho, +1 base 1
        const tipo = (linha["Tipo"] || "").trim().toUpperCase()
        const idCsv = (linha["Id"] || "").trim()

        try {
            if (tipo === "C") {
                const existente = idCsv ? idx.cat.get(idCsv) : null
                const resp = await cadastrarCategoria(
                    idCardapio,
                    dataCategoria(linha, existente, ordemC++, estabelecimento),
                    autorizacao,
                    false
                )
                const ok = registrar(resp, existente, `Linha ${numero} (Categoria "${linha["Nome"]}")`)
                categoriaIdAtual = ok?.id ?? existente?.id ?? null
                produtoIdAtual = null
                grupoIdAtual = null
                ordemP = 0
            } else if (tipo === "P") {
                if (!categoriaIdAtual) {
                    resultado.erros.push(`Linha ${numero}: produto sem categoria acima.`)
                    continue
                }
                const existente = idCsv ? idx.prod.get(idCsv) : null
                const resp = await cadastrarProduto(
                    categoriaIdAtual,
                    dataProduto(linha, existente, ordemP++),
                    autorizacao,
                    false
                )
                const ok = registrar(resp, existente, `Linha ${numero} (Produto "${linha["Nome"]}")`)
                produtoIdAtual = ok?.id ?? existente?.id ?? null
                grupoIdAtual = null
                ordemG = 0
            } else if (["GA", "GQ", "GD", "GO"].includes(tipo)) {
                if (!produtoIdAtual) {
                    resultado.erros.push(`Linha ${numero}: grupo sem produto acima.`)
                    continue
                }
                const existente = idCsv ? idx.grupo.get(idCsv) : null
                const resp = await cadastrarGrupoDeOpcao(
                    produtoIdAtual,
                    dataGrupo(linha, existente, ordemG++),
                    autorizacao,
                    false
                )
                const ok = registrar(resp, existente, `Linha ${numero} (Grupo "${linha["Nome"]}")`)
                grupoIdAtual = ok?.id ?? existente?.id ?? null
                ordemO = 0
            } else if (tipo === "O") {
                if (!grupoIdAtual) {
                    resultado.erros.push(`Linha ${numero}: opção sem grupo acima.`)
                    continue
                }
                const existente = idCsv ? idx.opc.get(idCsv) : null
                const resp = await cadastrarOpcao(
                    grupoIdAtual,
                    dataOpcao(linha, existente, ordemO++),
                    autorizacao,
                    false
                )
                registrar(resp, existente, `Linha ${numero} (Opção "${linha["Nome"]}")`)
            } else {
                resultado.erros.push(`Linha ${numero}: tipo desconhecido "${tipo}".`)
            }
        } catch (e) {
            resultado.erros.push(`Linha ${numero}: ${e?.message || e}`)
        }
    }

    // Um unico snapshot no historico ao final (os cadastros acima foram com
    // registrarHistorico=false), gerando apenas UM log em vez de um por linha.
    if (resultado.criados + resultado.atualizados > 0) {
        try {
            await registrarHistoricoCardapio(estabelecimento?.id, autorizacao)
        } catch (e) {
            // Nao falha a importacao se o snapshot do historico der erro.
        }
    }

    return resultado
}

// "Apagar e recriar": inativa o cardapio atual e cria um NOVO vazio no backend (os itens
// antigos somem do editor, sem virar "inativos" e sem apagar dados), depois importa o
// arquivo no cardapio novo. Usado quando o CSV nao tem Ids e o usuario quer SUBSTITUIR o
// cardapio em vez de duplicar.
export async function substituirCardapioComCsv({ texto, estabelecimento, autorizacao }) {
    const resp = await substituirPorCardapioNovo(estabelecimento?.id, autorizacao)
    if (resp && resp.retornoErro) {
        return { criados: 0, atualizados: 0, erros: [resp.mensagem || "Erro ao recriar o cardápio."] }
    }
    return importarCsvCardapio({ texto, estabelecimento, autorizacao })
}
